<template>
  <section id="dashboard" class="page active">
    <div class="wrap">
      <div class="inner-top">
        <div class="eyebrow">My portfolio</div>
        <div class="row">
          <h1 class="title">{{ t("dash_title") }}</h1>
          <span class="tag">{{ t("dash_tag") }}</span>
        </div>
        <p class="subtext">{{ accountLabel }}</p>
      </div>
      <div class="dashboard-stats">
        <div class="panel metric">
          <label>{{ t("m1") }}</label>
          <strong>{{ totalPurchased }}</strong>
          <small>{{ t("m1u") }}</small>
        </div>
        <div class="panel metric">
          <label>{{ t("m2") }}</label>
          <strong>{{ spentDisplay }}</strong>
          <small>{{ t("m2u") }}</small>
        </div>
        <div class="panel metric">
          <label>{{ t("m3") }}</label>
          <strong>{{ directCount }}</strong>
          <small>{{ teamNote }}</small>
        </div>
        <div class="panel metric">
          <label>{{ t("m4") }}</label>
          <strong>{{ rewardDisplay }}</strong>
          <small>{{ t("m4u") }}</small>
        </div>
      </div>
      <div class="dash-grid">
        <div class="panel">
          <div class="tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="{ active: selectedTable === tab.id }"
              @click="selectedTable = tab.id"
            >
              {{ t(tab.labelKey) }}
            </button>
          </div>
          <div class="table-wrap">
            <table v-if="selectedTable === 'purchases' && allOrders.length">
              <thead>
                <tr>
                  <th>{{ t("th_project") }}</th>
                  <th>{{ t("th_qty") }}</th>
                  <th>{{ t("th_coin") }}</th>
                  <th>{{ t("th_spent") }}</th>
                  <th>{{ t("th_status") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(o, idx) in ordersReversed" :key="'o-' + idx">
                  <td>
                    {{ o.project }}
                    <div class="subtext">{{ o.date }}</div>
                  </td>
                  <td>{{ o.qty }} {{ t("stat2u") }}</td>
                  <td>{{ o.coin }}</td>
                  <td>{{ fmtCoin(o.spent, o.coin) }}</td>
                  <td>{{ t("status_done") }}</td>
                </tr>
              </tbody>
            </table>
            <table v-else-if="selectedTable === 'team' && teamRows.length">
              <thead>
                <tr>
                  <th>{{ t("th_team_no") }}</th>
                  <th>{{ t("th_team_addr") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in teamRows" :key="row.address">
                  <td>{{ row.index }}</td>
                  <td :title="row.address">{{ row.short }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty">{{ emptyText }}</div>
          </div>
        </div>
        <div>
          <div class="panel">
            <h3>{{ t("share_title") }}</h3>
            <p class="subtext" v-html="t('share_sub')"></p>
            <div class="copy-box">
              <input class="input" :value="refLink" readonly aria-label="个人推荐链接" />
              <button type="button" class="btn small" @click="$emit('copy-ref')">
                {{ t("copy") }}
              </button>
            </div>
            <p class="subtext">{{ t("share_note") }}</p>
          </div>
          <div class="panel" style="margin-top: 18px">
            <h3>{{ t("my_nft") }}</h3>
            <div v-if="!nftRows.length" class="empty">{{ t("empty_p") }}</div>
            <div v-for="row in nftRows" :key="row.id" class="row nft-row">
              <i class="dash-logo"></i>
              <div>
                <b style="font-weight: 400">{{ row.name }}</b>
                <p class="subtext">{{ fmt("my_hold", { n: row.qty }) }}</p>
              </div>
              <button type="button" class="btn small" @click="$emit('open-project', row.id)">
                {{ t("view") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { fmtCoin } from "@/film/pricing";
import { shortenAddress } from "@/film/format";
import {
  createEmptyCoinAmounts,
  formatCoinAmountsLine,
} from "@/film/chainDashboard";

const ADDR_RE = /^0x[a-fA-F0-9]{40}$/;

export default {
  name: "DashboardPage",
  props: {
    t: { type: Function, required: true },
    fmt: { type: Function, required: true },
    orders: { type: Array, default: () => [] },
    directShare: { type: Array, default: () => [] },
    walletConnected: { type: Boolean, default: false },
    walletAddress: { type: String, default: "" },
    refLink: { type: String, default: "" },
    subscribedByCoin: {
      type: Object,
      default: () => createEmptyCoinAmounts(),
    },
    rewardedByCoin: {
      type: Object,
      default: () => createEmptyCoinAmounts(),
    },
  },
  data() {
    return {
      selectedTable: "purchases",
      tabs: [
        { id: "purchases", labelKey: "tab_p" },
        { id: "team", labelKey: "tab_t" },
      ],
    };
  },
  computed: {
    allOrders() {
      return this.orders || [];
    },
    ordersReversed() {
      return [...this.allOrders].reverse();
    },
    teamRows() {
      const list = this.directShare || [];
      const rows = [];
      list.forEach((raw, i) => {
        const address = String(raw || "").trim();
        if (!ADDR_RE.test(address)) return;
        rows.push({
          index: rows.length + 1,
          address,
          short: shortenAddress(address),
        });
      });
      return rows;
    },
    totalPurchased() {
      return this.orders.reduce((s, o) => s + o.qty, 0);
    },
    spentDisplay() {
      return formatCoinAmountsLine(this.subscribedByCoin);
    },
    directCount() {
      return this.teamRows.length;
    },
    teamNote() {
      return this.fmt("m3u", { team: this.directCount });
    },
    rewardDisplay() {
      return formatCoinAmountsLine(this.rewardedByCoin);
    },
    accountLabel() {
      if (this.walletConnected && this.walletAddress) return this.walletAddress;
      return this.t("acc_off");
    },
    nftRows() {
      const byProject = new Map();
      for (const o of this.orders || []) {
        const qty = Math.max(0, Number(o.qty) || 0);
        if (qty <= 0) continue;
        const id = o.projectId;
        if (!id) continue;
        if (!byProject.has(id)) {
          byProject.set(id, { id, name: o.project, qty: 0 });
        }
        byProject.get(id).qty += qty;
      }
      return [...byProject.values()];
    },
    emptyText() {
      if (this.selectedTable === "purchases") return this.t("empty_p");
      return this.t("empty_t");
    },
  },
  methods: {
    fmtCoin,
  },
};
</script>
